import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import Cookies from "js-cookie";
import api from "../libs/axios";
import { useNavigate } from "react-router-dom";
import Loader from "../utils/loader";

interface UserDetails {
    id: number;
    user_id: number;
    first_name: string;
    last_name: string;
    email: string;
    date_of_birth: string;
    gender: string;
    profile_picture: string | null;
    address: string;
    city: string;
    state: string;
    country: string;
    pincode: string;
    created_at: string;
    updated_at: string;
}

interface User {
    id: number;
    phone_number: string;
    role: string;
    details: UserDetails;
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    login: (token: string, rememberMe?: boolean) => Promise<void>;
    logout: () => void;
    fetchUser: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(Cookies.get("__token") || null);
    const navigate = useNavigate();

    const fetchUser = async () => {
        try {
            const res = await api.get("/auth/profile");
            setUser(res.data.user);
        } catch (error) {
            logout();
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            fetchUser();
        } else {
            setLoading(false);
        }
    }, [token]);

    const login = async (token: string, rememberMe?: boolean) => {
        try {
            // SEC-11: never sent over plain http, and not sent on requests from other sites.
            Cookies.set("__token", token, {
                expires: rememberMe ? 7 : 1,
                path: "/",
                sameSite: "strict",
                secure: window.location.protocol === "https:",
            });
            setToken(token);
            navigate("/");
        } catch (error: any) {
            console.error("Login failed:", error);
        }
    };

    const logout = () => {
        // SEC-11: end the session on the server too, so a copied token stops working.
        // Best effort: the panel signs out locally whatever the answer.
        const current = Cookies.get("__token");
        if (current) {
            // The token is passed here because the cookie is removed before the request goes out.
            api.post("/auth/logout", {}, { headers: { Authorization: `Bearer ${current}` } }).catch(() => undefined);
        }
        Cookies.remove("__token", { path: "/" });
        setToken(null);
        setUser(null);
        navigate("/");
    };

    return (
        <AuthContext.Provider value={{ user, fetchUser, token, login, logout }}>
            {loading ? (
                <Loader />
            ) : (
                children
            )}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

export default AuthProvider;